import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MailLogFilters, MailSuppressionReason } from '@peles/shared';
import * as mailLogApi from '../api/mailLog';
import type { MailLogScope } from '../api/mailLog';

export const MAIL_LOG_KEY = ['mail-log'] as const;
export const MAIL_SUPPRESSIONS_KEY = ['mail-suppressions'] as const;

/** A page of the log. `enabled` false for a scope the caller has not settled yet. */
export function useMailLog(filters: MailLogFilters, enabled = true) {
  return useQuery({
    queryKey: [...MAIL_LOG_KEY, 'list', filters],
    queryFn: () => mailLogApi.listMailLog(filters),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useMailCategories(filters: MailLogFilters, enabled = true) {
  return useQuery({
    queryKey: [...MAIL_LOG_KEY, 'categories', filters],
    queryFn: () => mailLogApi.listMailCategories(filters),
    enabled,
  });
}

/** One message with what was sent; fetched only when it is opened. */
export function useMailLogEntry(id: string | null, scope: MailLogScope) {
  return useQuery({
    queryKey: [...MAIL_LOG_KEY, 'entry', id, scope],
    queryFn: () => mailLogApi.getMailLogEntry(id as string, scope),
    enabled: id !== null,
  });
}

export function useResendMail(scope: MailLogScope) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mailLogApi.resendMail(id, scope),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MAIL_LOG_KEY }),
  });
}

export function useMailSuppressions(query: {
  search?: string;
  reasons?: MailSuppressionReason[];
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: [...MAIL_SUPPRESSIONS_KEY, query],
    queryFn: () => mailLogApi.listMailSuppressions(query),
    placeholderData: keepPreviousData,
  });
}

export function useAddMailSuppression() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { address: string; detail?: string }) =>
      mailLogApi.addMailSuppression(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MAIL_SUPPRESSIONS_KEY }),
  });
}

export function useRemoveMailSuppression() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (address: string) => mailLogApi.removeMailSuppression(address),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MAIL_SUPPRESSIONS_KEY }),
  });
}
